import React from 'react';
import { createTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';

import '@fontsource/lato';

export const preloadFonts = ['Lato'];

const Theme = () => {
  const mode = useSelector((state) => state.theme.paletteMode);
  const theme = React.useMemo(
    () =>
      createTheme({
        direction: 'ltr',
        breakpoints: {
          values: {
            xs: 0,
            sm: 600, //375
            md: 900, //768
            lg: 1200,
            xl: 1536,
            laptop: 1024,
          },
        },
        palette: {
          mode: mode,
          ...(mode === 'light'
            ? {
                primary: { main: '#215382', outline: '#F6F6F6' }, // 1E97B9
                secondary: { main: '#4D759B' },
                error: { main: '#EC5962' },
                warning: { main: '#FFA747' },
                success: { main: '#33B784' },
                primary50: '#DFEBF7',                
                primary100:'#BAD5ED',
                primary200: '#91BCE3',
                primary300: '#5899d5',
                primary400: '#3078bb',
                gray10: '#D1D5DB',
                gray30: '#9CA3Af',
                gray50: '#6B7280',
                gray70: '#4E535D',
                gray90: '#34373E',
              }
            : {
                primary: { main: '#3498DB', outline: '#3A4556' },
                secondary: { main: '#5A8CAE' },
                error: { main: '#FF5252' },
                warning: { main: '#FFB74D' },
                success: { main: '#2ECC71' },
                primary50: '#2C3E50',
                primary100:'#3499DB',
                gray10: '#353B49',
                gray30: '#4E535D',
                gray50: '#6B7280',
                gray70: '#9CA3AF',
                gray90: '#D1D5DB',
              }),
          info: { main: '#52C4E4' },
          controls: { main: '#34373E' },
          pdark: {
            light: '#125A6E',
            main: '#125A6E',
            dark: '#125A6E',
            contrastText: '#FFFFFF',
          },
          plight: {
            light: '#65CAE6',
            main: '#65CAE6',
            dark: '#65CAE6',
            contrastText: '#FFFFFF',
          },
          inactive: {
            main: '#D9D9D9',
            light: '#D9D9D9',
            dark: '#D9D9D9',
            contrastText: '#FFFFFF',
          },
          chipbg:{
            yellow1:'#F39E151A',
            errlight1:'#EC596233',
          },
          gray100:'#6B7280',
          gray20: '#EAEAEA',
          gray150: '#6B7280',
          success80: '#27AE60',
          success10: '#00A666',
          warning1: '#FDA240',
          ...(mode === 'light'
            ? {
                text: {
                  primary: '#34373E',
                  secondary: '#4E535D',
                  disabled: '#D1D5DB',
                  primaryChannel: '0 0 0',
                  secondaryChannel: '0 0 0',
                  black100:'#000000'
                },
                divider: 'rgba(234, 234, 234, 0.25)',
                background: {
                  paper: '#fff',
                  default: '#fff',
                  light1: '#F6F6F6',
                  light2: '#F5F5FA',
                  light3: '#0083FF24', // 14% = 24H
                  light4: '#F5F5F5',
                  light5: '#E9F5F8',
                  light6: '#F6F6F6',
                  light7: '#F6F6F6',
                  // light8: '#F5F5FA',
                  main: '#FFFFFF',
                },
                chipBackground: {
                  low: '#CCEDE0',
                  mild: '#E9F5F8',
                  medium: '#FFF2CC',
                  high: '#FDE9EC',
                  greylight: '#EFF3FD',
                  default: '#CCEDE0',
                  error: '#FEEFF0',
                },         
              }
            : {
                text: {
                  primary: '#fff',
                  disabled: 'rgba(55, 65, 81, 0.5)',
                  black100:'#ffffff'
                },
                divider: 'rgba(255, 255, 255, 0.12)',
                background: {
                  paper: '#121212',
                  default: '#1E2A37',
                  light2: '#262F3D',
                  light5: '#2C3E50',
                  light6: '#262F3D',
                  light7: '#4A5568',
                  // light8: '#262F3D',
                  main: '#1E2A37',
                },
                chipBackground: {
                  low: '#1E3D34',
                  mild: '#2C3E50',
                  medium: '#F39E151A',
                  high: '#4A1E1F',
                  greylight: '#1E2A37',
                  default: '#1E3D34',
                },
              }),
          dBlue: {
            main: '#566ED7',
            light: '#566ED7',
            dark: '#566ED7',
          },
          tertiaryBtn: {
            main: '#F2F3F5',
            contrastText: '#000',
          },
        },
        typography: {
          fontFamily: preloadFonts.join(','),
          fontWeightLight: 300,
          fontWeightRegular: 400,
          fontWeightMedium: 600,
          fontWeightBold: 700,
          h1: {
            fontWeight: 400,
            fontSize: 34,
            lineHeight: 1.24,
            letterSpacing: 0,
            '@media (max-width:1440px)': {
              fontSize: 26,
            },
            '@media (max-width:900px)': {
              fontSize: 18,
            },
          },
          h1b: {
            fontWeight: 700,
            fontSize: 34,
            lineHeight: 1.24,
            letterSpacing: 0.25,
            '@media (max-width:900px)': {
              fontSize: 32,
            },
          },
          h2: {
            fontWeight: 400,
            fontSize: 24,
            lineHeight: 1.333,
            letterSpacing: 0,
            '@media (max-width:1200px)': {
              fontSize: 18,
            },
            '@media (max-width:900px)': {
              fontSize: 14,
            },
          },
          h2bv: {
            fontWeight: 700,
            fontSize: 26,
            lineHeight: 1.333,
            letterSpacing: 0,
            '@media (max-width:1440px)': {
              fontSize: 20,
            },
            '@media (max-width:900px)': {
              fontSize: 18,
            },
          },
          h2b: {
            fontWeight: 700,
            fontSize: 26,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          h3: {
            fontWeight: 400,
            fontSize: 20,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          h3v: {
            fontWeight: 400,
            fontSize: 20,
            lineHeight: 1.333,
            letterSpacing: 0,
            '@media (max-width:1440px)': {
              fontSize: 18,
            },
            '@media (max-width:900px)': {
              fontSize: 16,
            },
          },
          h3b: {
            fontWeight: 700,
            fontSize: 20,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          h4: {
            fontWeight: 400,
            fontSize: 18,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          h4b: {
            fontWeight: 700,
            fontSize: 18,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          h5: {
            fontWeight: 400,
            fontSize: 18,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          h6: {
            fontWeight: 400,
            fontSize: 18,
            lineHeight: 1.333,
            letterSpacing: 0,
          },
          body1: {
            // body 16
            fontWeight: 400,
            fontSize: 16,
            lineHeight: 1.5,
            letterSpacing: 0,
          },
          body1bv: {
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1.5,
            letterSpacing: 0,
            '@media (max-width:1440px)': {
              fontSize: 14,
            },
            '@media (max-width:900px)': {
              fontSize: 12,
            },
          },
          body1b: {
            // body 16
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1.5,
            letterSpacing: 0,
          },
          body1wt: {
            // body 16
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1.5,
            letterSpacing: 0,
          },
          body2: {
            // body 14
            fontWeight: 400,
            fontSize: 14,
            lineHeight: 1.4,
            letterSpacing: 0,
          },
          body2b: {
            // body 14
            fontWeight: 700,
            fontSize: 14,
            lineHeight: 1.4,
            letterSpacing: 0,
          },
          subtitle1: {
            // subtitle 12
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 1.2,
            letterSpacing: 0,
          },
          subtitle1v: {
            // subtitle 12
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 1.2,
            letterSpacing: 0,
            '@media (max-width:900px)': {
              fontSize: 10,
            },
          },
          subtitle1b: {
            // subtitle 12
            fontWeight: 700,
            fontSize: 12,
            lineHeight: 1.2,
            letterSpacing: 0,
          },
          caption: {
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: 0,
          },
          button: {
            fontWeight: 400,
            fontSize: 14,
            letterSpacing: 0,
          },
        },
        components: {
          MuiAutocomplete: {
            styleOverrides: {
              popper: {
                fontSize: '14px',
              },
              option: {
                fontSize: '14px',
              },
            },
          },
          MuiButtonBase: {
            styleOverrides: {
              root: {
                fontFamily: preloadFonts.join(','),
              },
            },
          },
          MuiButton: {
            defaultProps: {
              variant: 'outlined',
            },
            styleOverrides: {
              sizeSmall: {
                height: '32px',
              },
              root: {
                textTransform: 'none',
                borderRadius: 8,
                fontSize: '14px',
                height: '40px',
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: '#E9F5F8', // backgroundColor: (theme) => theme.palette.background.light5, rgba(0, 0, 0, 0.08)
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: {
                paddingLeft: '24px',
                paddingRight: '24px',
                paddingBottom: '24px',
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              sizeSmall: {
                height: '40px',
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: '#E9F5F8',
                },
                fontSize: '14px',
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                fontSize: '14px',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              margin: 'dense',
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: '14px',
                fontFamily: preloadFonts.join(','),
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                color: '#EAEAEA',
              },
            },
          },
          MuiFormControlLabel: {
            styleOverrides: {
              label: {
                fontSize: '14px',
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                fontSize: '14px',
                '&.Mui-checked': { color: '#215382' },
              },
            },
          },
        },
      }),
    [mode],
  );

  return theme;
};

export default Theme;
